import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

export const initSentry = () => {
  // Initialize Sentry
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE;
  const version = import.meta.env.VITE_APP_VERSION || '0.0.1';
  
  // Only initialize if DSN is provided
  if (!dsn && import.meta.env.PROD) {
    console.warn('Sentry DSN not found in environment variables. Error tracking is disabled.');
    return;
  }
  
  Sentry.init({
    dsn,
    integrations: [new BrowserTracing()],
    
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring
    // We recommend adjusting this value in production
    tracesSampleRate: 1.0,
    
    // Only capture errors in production
    enabled: import.meta.env.PROD || environment === 'staging',
    
    // Capture environment
    environment,
    
    // Release version, should match your deployment version
    release: version,
    
    // Attach Redux state to error reports (useful for debugging)
    beforeSend(event) {
      // Don't send events in development
      if (!import.meta.env.PROD && environment !== 'staging') {
        return null;
      }
      return event;
    },
  });
};

// Custom error boundary component
export const SentryErrorBoundary = Sentry.ErrorBoundary;

// Helper function to capture exceptions manually
export const captureException = (error: any, context?: Record<string, any>) => {
  if (error) {
    Sentry.captureException(error, {
      extra: context
    });
  }
};

// Helper function to capture messages
export const captureMessage = (message: string, level?: Sentry.Severity) => {
  Sentry.captureMessage(message, level);
};

// Helper function to set user information 
export const setUser = (user: { id?: string; email?: string; username?: string }) => {
  Sentry.setUser(user);
};

// Helper function to add breadcrumbs
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

// Helper function to start transaction
export const startTransaction = (context: Sentry.TransactionContext) => {
  return Sentry.startTransaction(context);
};

// Custom API error handler
export const handleApiError = (error: any, { endpoint, payload }: { endpoint: string; payload?: any }) => {
  captureException(error, {
    endpoint,
    payload: JSON.stringify(payload),
    message: error.message
  });
  
  // You can also report this to your analytics service
  const analyticsData = {
    category: 'API_ERROR',
    action: endpoint,
    label: error.message
  };
  
  // Track event with your analytics provider 
  // e.g., window.analytics?.track('API Error', analyticsData);
  
  // Return a user-friendly message
  return {
    error: true,
    message: 'Something went wrong. Our team has been notified.'
  };
};
